import { toast } from 'react-toastify';
import { CreateWarehouseBody, UpdateWarehouseBody, Warehouse } from '@core/models/warehouse.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import WarehouseService from 'services/api/warehouse.service';

// -------------------------------------------------------------------------

interface IWarehouseState {
    loading: boolean;
    error: IErrorModel | undefined;
    warehouses: Warehouse[];
    warehouse: Warehouse | null;
}

const initialState: IWarehouseState = {
    loading: false,
    error: {} as IErrorModel,
    warehouses: [],
    warehouse: null,
};

// -------------------------------------------------------------------------

export const getWarehousesThunk = createAsyncThunk<Warehouse[], void, { rejectValue: IErrorModel }>(
    `/warehouse/getWarehouses`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await WarehouseService.getWarehouses();
            return response.data.warehouses;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch warehouses', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getWarehouseByIdThunk = createAsyncThunk<Warehouse, number, { rejectValue: IErrorModel }>(
    `/warehouse/getWarehouseById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await WarehouseService.getWarehouseById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch warehouse', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createWarehouseThunk = createAsyncThunk<
    ISuccessResponse,
    CreateWarehouseBody,
    { rejectValue: IErrorModel }
>(`/warehouse/createWarehouse`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await WarehouseService.createWarehouse(body);
        toast.success(response.data.message || 'Warehouse created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create warehouse', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const updateWarehouseThunk = createAsyncThunk<
    ISuccessResponse,
    UpdateWarehouseBody,
    { rejectValue: IErrorModel }
>(`/warehouse/updateWarehouse`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await WarehouseService.updateWarehouse(body);
        toast.success(response.data.message || 'Warehouse updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update warehouse', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteWarehouseThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/warehouse/deleteWarehouse`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await WarehouseService.deleteWarehouse(id);
            toast.success(response.data.message || 'Warehouse deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete warehouse', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const warehouseSlice = createSlice({
    name: `warehouse`,
    initialState,
    reducers: {
        clearWarehouse(state) {
            state.warehouse = null;
        },
    },
    extraReducers: (builder) => {
        // Get Warehouses
        builder.addCase(getWarehousesThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getWarehousesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.warehouses = action.payload;
        });
        builder.addCase(getWarehousesThunk.rejected, (state, action) => {
            state.loading = false;
            state.warehouses = [];
            state.error = action.payload;
        });

        // Get Warehouse By Id
        builder.addCase(getWarehouseByIdThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getWarehouseByIdThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.warehouse = action.payload;
        });
        builder.addCase(getWarehouseByIdThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Create Warehouse
        builder.addCase(createWarehouseThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createWarehouseThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(createWarehouseThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Update Warehouse
        builder.addCase(updateWarehouseThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateWarehouseThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(updateWarehouseThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Delete Warehouse
        builder.addCase(deleteWarehouseThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteWarehouseThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteWarehouseThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default warehouseSlice;
export const { clearWarehouse } = warehouseSlice.actions;
export const warehouseReducer = warehouseSlice.reducer;

